import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  SelectGroup, SelectLabel,
} from '@/components/ui/select';
import { RefreshCw, Loader2, AlertTriangle, Power, ArrowUp, ArrowDown, ShieldAlert } from 'lucide-react';
import {
  forexService,
  fmtForexSymbol,
  instrumentClassLabel,
  type ForexService,
  type FxcmHealth,
  type FxcmSettings,
  type FxcmAccount,
  type FxcmPosition,
  type FxcmOrder,
  type FxcmClosedTrade,
  type FxcmInstrument,
  type FxcmQuote,
  type ModifyProtectiveOrders,
  type PlaceMarketOrder,
  type PlaceEntryOrder,
  type FxcmTradesPayload,
  type FxcmTradeHistoryPage,
} from '@/services/forexService';
import signalRService from '@/services/signalRService';
import { ForexChart } from './ForexChart';

// Order types the ticket can build. market/marketRange → immediate;
// limit/stop/range → pending entry order.
type OrderType = 'market' | 'marketRange' | 'limit' | 'stop' | 'range';

// A validated order ready to submit. Discriminated so the confirm dialog
// and submit path stay type-safe across the market vs entry shapes.
type PendingSubmit =
  | { kind: 'market'; order: PlaceMarketOrder }
  | { kind: 'entry'; order: PlaceEntryOrder };

/** A [min,max] band `pips` wide on each side of `ref`, in the instrument's
 *  point size. Used for Market Range / Range Entry orders. null if inputs
 *  are unusable (no reference price, no point size, non-positive range). */
function computeBand(ref: number, pips: number, pointSize: number): { min: number; max: number } | null {
  if (!(ref > 0) || !(pips > 0) || !(pointSize > 0)) return null;
  const half = pips * pointSize;
  return { min: ref - half, max: ref + half };
}

const ORDER_TYPE_LABEL: Record<OrderType, string> = {
  market: 'Market',
  marketRange: 'Market Range',
  limit: 'Limit',
  stop: 'Stop',
  range: 'Range Entry',
};

/** Whether a recent-orders row is still cancellable. Only an order
 *  resting on the book qualifies. FXCM's working status is the short code
 *  "W" (Waiting); some paths surface a verbose form. This is an ALLOWLIST,
 *  not a terminal-substring blocklist — the SDK propagates raw coded
 *  statuses (W / C / R / …) that contain no English words, so a blocklist
 *  would misread terminal codes as cancellable and offer a Cancel the
 *  broker just rejects. Unknown statuses are treated as non-cancellable. */
function isCancellable(status: string): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return s === 'w' || s.includes('wait') || s.includes('working');
}

// Fallback for the order-entry picker before the live instrument list
// loads (or if the instruments endpoint fails) — a few majors so the
// dropdown is never empty. The real list comes from svc.getInstruments().
// baseUnitSize is 0 here — it's account-specific and only known once the
// live instrument list loads from FXCM. Until then the order form shows no
// unit estimate; it never guesses a size.
const FALLBACK_INSTRUMENTS: FxcmInstrument[] = [
  { symbol: 'EURUSD', displaySymbol: 'EUR/USD', instrumentClass: 'forex', contractMultiplier: 1, pointSize: 0.0001, isTradingAllowed: true, baseUnitSize: 0 },
  { symbol: 'GBPUSD', displaySymbol: 'GBP/USD', instrumentClass: 'forex', contractMultiplier: 1, pointSize: 0.0001, isTradingAllowed: true, baseUnitSize: 0 },
  { symbol: 'USDJPY', displaySymbol: 'USD/JPY', instrumentClass: 'forex', contractMultiplier: 1, pointSize: 0.01, isTradingAllowed: true, baseUnitSize: 0 },
];

function fmtMoney(n: number | null | undefined, frac = 2): string {
  if (n == null) return '-';
  return n.toLocaleString('en-US', { minimumFractionDigits: frac, maximumFractionDigits: frac });
}

function fmtRate(n: number | null | undefined, symbol?: string): string {
  if (n == null) return '-';
  // JPY pairs quote to 3 decimals, everything else to 5. Standard forex convention.
  const isJpy = (symbol ?? '').toUpperCase().includes('JPY');
  return n.toLocaleString('en-US', {
    minimumFractionDigits: isJpy ? 3 : 5,
    maximumFractionDigits: isJpy ? 3 : 5,
  });
}

function pnlClass(n: number | null | undefined): string {
  if (n == null || n === 0) return '';
  return n > 0 ? 'text-green-500' : 'text-red-500';
}

// FXCM order-type codes → human labels. Covers the contingent (S/L) and entry
// (LE/SE/RE + trailing) families; unknown codes pass through verbatim.
const ORDER_TYPE_LABELS: Record<string, string> = {
  S: 'Stop', L: 'Limit', ST: 'Trail stop',
  SE: 'Stop entry', LE: 'Limit entry', RE: 'Range entry',
  STE: 'Trail stop entry', LTE: 'Trail limit entry',
  O: 'Open', C: 'Close', CM: 'Close (mkt)', OM: 'Open (mkt)',
};
function orderTypeLabel(code: string): string {
  return ORDER_TYPE_LABELS[code] ?? (code || '—');
}

const CLOSE_REASON_LABEL: Record<string, string> = {
  stop: 'Stop-loss',
  takeProfit: 'Take-profit',
  manual: 'Manual',
  marginCall: 'Margin call',
};

/** Human label for a closed trade's reason. Empty → "—". A guessed reason
 *  (rate-matched against the trade's saved stop/limit, not an FXCM field) is
 *  marked with a trailing "?" and a tooltip so it's never mistaken for fact. */
function closeReasonLabel(reason: string, isGuess: boolean): { text: string; guess: boolean } {
  const text = CLOSE_REASON_LABEL[reason];
  if (!text) return { text: '—', guess: false };
  return { text: isGuess ? `${text} ?` : text, guess: isGuess };
}

/** Parse a user-entered numeric field strictly: the whole trimmed string
 *  must be a finite positive number. Returns null otherwise. Unlike
 *  parseFloat, Number() rejects partial input ("1abc", "1,0950" → NaN),
 *  so a malformed lot / stop / limit entry can't be silently truncated
 *  into a wrong value on these risk-bearing controls. */
function strictPositiveNumber(raw: string): number | null {
  const n = Number(raw.trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Stable signature of a closed-trade set — used to decide whether a realtime
// ForexTrades push actually carries a NEW (or enriched) close vs. the same set
// re-sent on a position P&L tick. Mirrors the backend liveSig fields, including
// closeReason: a statement-imported trade can later be enriched with the live
// close reason (same id/time/netPL), and the table must refetch when it is.
// Assumes the payload's wire order is stable for stable data (the backend returns
// a deterministic order), so the sig only changes when the data actually changes.
const closedSig = (xs: FxcmClosedTrade[]) =>
  xs.map(t => `${t.tradeId}:${t.closeTime}:${t.netPL}:${t.closeReason}:${t.closeReasonIsGuess ? 1 : 0}`).join('|');

/**
 * FXCM forex trading panel. Used in two places, distinguished by props:
 *   • Paper Trading → Forex tab — demo account (svc=forexService).
 *   • Live Trading page — funded real account (svc=forexLiveService,
 *     live=true): adds a red real-money banner and a mandatory order
 *     confirmation step before anything is submitted.
 */
export function ForexPanel({
  svc = forexService,
  live = false,
}: {
  svc?: ForexService;
  live?: boolean;
}) {
  const [health, setHealth] = useState<FxcmHealth | null>(null);
  const [settings, setSettings] = useState<FxcmSettings | null>(null);
  const [account, setAccount] = useState<FxcmAccount | null>(null);
  const [positions, setPositions] = useState<FxcmPosition[]>([]);
  const [orders, setOrders] = useState<FxcmOrder[]>([]);
  const [closedTrades, setClosedTrades] = useState<FxcmClosedTrade[]>([]);
  // Persistent trade history (paginated, store-backed). Separate from the realtime
  // closedTrades push, which only signals that a new close happened → refetch page 1.
  const [historyPage, setHistoryPage] = useState<FxcmTradeHistoryPage | null>(null);
  const [historyPageNum, setHistoryPageNum] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const historyPageRef = React.useRef(1);
  const historyReqRef = React.useRef(0);          // monotonic request id — used for ordering, not strict equality
  const lastAppliedSeqRef = React.useRef(0);      // seq of the newest /history response we committed; older successes are ignored
  const lastClosedSigRef = React.useRef('');      // last seen closed-trade set; refetch page 1 only when it changes
  const lastGoodPageRef = React.useRef(1);        // last page /history actually returned, to snap back on a failed load
  const loadHistoryRef = React.useRef<(p: number) => void>(() => {}); // late-bound so loadAll can call it without a TDZ
  const historyHydratedRef = React.useRef(false); // first loadAll only seeds the gate; later reconciles can refetch
  const syncMsgTimerRef = React.useRef<number | null>(null); // pending auto-clear of the Sync success message
  const HISTORY_PAGE_SIZE = 25;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tradeable instruments (forex + commodities + indices + crypto …).
  // Fetched once when the broker first becomes reachable; FXCM's
  // instrument set doesn't change intraday.
  const [instruments, setInstruments] = useState<FxcmInstrument[]>(FALLBACK_INSTRUMENTS);
  // One-shot guard for the instrument fetch. A ref (not a setState
  // updater side-effect) so it stays a pure render and StrictMode's
  // double-invoke can't fire two requests. Reset on failure to retry.
  const instrumentsFetched = React.useRef(false);
  // Synchronous re-entrancy guard for order submission — `orderSubmitting`
  // state only disables the button after a re-render, so a fast double
  // click could otherwise fire two identical (real-money) orders.
  const submitGuardRef = React.useRef(false);
  // Same re-entrancy guard for the live close/cancel confirmation button —
  // its action runs synchronously on click, before the card re-renders away.
  const actionGuardRef = React.useRef(false);

  // Live bid/ask per instrument — polled on a fast cadence (separate from
  // the 15s account/positions refresh) so the quote strip feels live.
  const [quotesBySymbol, setQuotesBySymbol] = useState<Map<string, FxcmQuote>>(new Map());

  // Quick-order form state
  const [orderSymbol, setOrderSymbol] = useState('EURUSD');
  const [orderSide, setOrderSide] = useState<'buy' | 'sell'>('buy');
  // Order type drives which extra fields show. Combined with side this
  // gives the named types: buy+limit = Buy Limit, sell+stop = Sell Stop…
  const [orderType, setOrderType] = useState<OrderType>('market');
  const [orderEntryPrice, setOrderEntryPrice] = useState(''); // trigger rate — limit/stop/range
  const [orderRangePips, setOrderRangePips] = useState('');   // band half-width — marketRange/range
  // Order size in FXCM contracts. 1 forex contract = 1,000 units of base
  // currency (one micro lot); contracts are whole numbers. Per-contract
  // size for non-forex instruments comes from the instrument's baseUnitSize.
  const [orderContracts, setOrderContracts] = useState('1');
  const [orderStop, setOrderStop] = useState('');       // optional stop-loss rate
  const [orderTrailPips, setOrderTrailPips] = useState(''); // optional trailing-stop step (pips)
  const [orderLimit, setOrderLimit] = useState('');     // optional take-profit rate
  const [orderTif, setOrderTif] = useState('');         // time-in-force ('' = broker default)
  const [orderExpireAt, setOrderExpireAt] = useState(''); // GTD expiry (datetime-local value)
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState<string | null>(null);
  // Live-trading only: a validated order parked awaiting explicit
  // confirmation. null = nothing pending. Demo submits straight through.
  const [confirmOrder, setConfirmOrder] = useState<PendingSubmit | null>(null);
  // Live-trading only: a pending close/cancel action awaiting confirmation
  // (these are real-money state changes too — same two-click guard).
  const [confirmAction, setConfirmAction] = useState<{ message: string; run: () => void } | null>(null);

  // Inline SL/TP editor for an open position (keyed by FXCM tradeId). Blank
  // input = leave that side unchanged; the per-side "clear" checkbox removes it.
  const [editingTradeId, setEditingTradeId] = useState<string | null>(null);
  const [editStop, setEditStop] = useState('');
  const [editLimit, setEditLimit] = useState('');
  const [editClearStop, setEditClearStop] = useState(false);
  const [editClearLimit, setEditClearLimit] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState<string | null>(null);
  const [editOk, setEditOk] = useState(false);   // colours editMsg green vs red

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      // Settings + health are cheap and tell us whether to bother with the rest.
      const [h, s] = await Promise.all([svc.health(), svc.getSettings()]);
      setHealth(h);
      setSettings(s);

      // Only call broker-touching endpoints if we have creds AND the broker is reachable.
      // Otherwise we'd just keep getting 404/500 every refresh.
      if (h.brokerReachable) {
        const [a, p, o, ct] = await Promise.all([
          svc.getAccount().catch(() => null),
          svc.getPositions().catch(() => [] as FxcmPosition[]),
          svc.getRecentOrders(50).catch(() => [] as FxcmOrder[]),
          svc.getRecentTrades(50).catch(() => [] as FxcmClosedTrade[]),
        ]);
        setAccount(a);
        setPositions(p);
        setOrders(o);
        setClosedTrades(ct);
        // Seed the realtime gate from the REST hydration so the FIRST ForexTrades push
        // (which carries this same set) is a no-op. The 30s reconcile reuses this path:
        // if it catches a close SignalR missed, the set changed since last seed — refresh
        // page 1 now, because reseeding the gate would otherwise hide it from the next
        // push too (the table renders historyPage.items, not closedTrades). The very
        // first hydration never refetches here — the page-change effect already loads
        // page 1 on mount, so this would just be a duplicate.
        {
          const newSig = closedSig(ct);
          if (historyHydratedRef.current) {
            if (lastClosedSigRef.current !== newSig && historyPageRef.current === 1) loadHistoryRef.current(1);
          } else {
            historyHydratedRef.current = true;
          }
          lastClosedSigRef.current = newSig;
        }
        // Instruments are static intraday — fetch once. The ref guard
        // keeps this out of the setState updater (must stay pure) and
        // prevents a duplicate request under StrictMode.
        if (!instrumentsFetched.current) {
          instrumentsFetched.current = true;
          svc.getInstruments()
            .then((list) => { if (list.length > 0) setInstruments(list); })
            .catch(() => { instrumentsFetched.current = false; /* retry next refresh */ });
        }
      } else {
        setAccount(null);
        setPositions([]);
        setOrders([]);
        setClosedTrades([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [svc]);

  useEffect(() => {
    void loadAll();
    // Slow safety reconcile only — account + trades arrive in real time over the
    // trading hub (see the SignalR effect below). This 30s tick is a fallback for
    // a missed push / dropped socket and also refreshes recent ORDERS, which the
    // realtime stream doesn't carry.
    const id = setInterval(() => void loadAll(), 30_000);
    return () => clearInterval(id);
  }, [loadAll]);

  const realtimeEnv = live ? 'Live' : 'Demo';

  // Paginated trade history (store-backed). Loads on page change and when a genuinely
  // new close arrives (see the realtime handler below). NOT gated on broker reachability
  // — /history reads the local store, so persisted trades stay viewable even while FXCM
  // is briefly down. (Sync, which calls FXCM, IS gated.)
  const loadHistory = useCallback(async (page: number) => {
    const seq = ++historyReqRef.current;
    setHistoryLoading(true);
    try {
      const p = await svc.getHistory(page, HISTORY_PAGE_SIZE);
      // Commit any success newer than what we last applied (don't drop an earlier
      // success just because a later request started after it), AND only for the page
      // the user currently wants — otherwise a late success for an abandoned page would
      // render its rows under a newer snap-back's label.
      if (seq > lastAppliedSeqRef.current && page === historyPageRef.current) {
        lastAppliedSeqRef.current = seq;
        setHistoryPage(p);
        lastGoodPageRef.current = page;
        setHistoryError(null);
      }
    } catch (e) {
      // Only the newest in-flight request drives the failure UX. Surface the error and
      // snap historyPageNum back to the page we still have rows for, so the range/label
      // don't claim a page whose data never arrived. (Re-fires the effect, reloading the
      // cached good page.)
      if (seq === historyReqRef.current) {
        setHistoryError(e instanceof Error ? e.message : String(e));
        setHistoryPageNum(lastGoodPageRef.current);
      }
    }
    finally { if (seq === historyReqRef.current) setHistoryLoading(false); }
  }, [svc]);
  loadHistoryRef.current = loadHistory;   // keep the late-bound ref pointed at the latest closure

  useEffect(() => {
    historyPageRef.current = historyPageNum;
    void loadHistory(historyPageNum);
  }, [historyPageNum, loadHistory]);

  // Clear any pending syncMsg auto-clear timer on unmount.
  useEffect(() => () => { if (syncMsgTimerRef.current) window.clearTimeout(syncMsgTimerRef.current); }, []);

  // Real-time account + trades over the trading hub — the panel no longer polls
  // for these. The backend pushes ForexAccount / ForexTrades to the per-(user,env)
  // group; the user id is taken from the authenticated connection server-side, so
  // a panel only ever receives its own account's stream. env keeps demo and live
  // on separate groups; only one panel route is mounted at a time.
  useEffect(() => {
    if (!(health?.brokerReachable)) return;
    const id = `forex-${realtimeEnv}`;
    let active = true;
    signalRService.addForexListener(id, 'forexAccount', (a: FxcmAccount) => setAccount(a));
    signalRService.addForexListener(id, 'forexTrades', (p: FxcmTradesPayload) => {
      setPositions(p.positions ?? []);
      const closed = p.closedTrades ?? [];
      setClosedTrades(closed);
      // The ForexTrades push fires on every position P&L move (~1.5s), so refresh
      // page 1 only when the CLOSED set actually changes — not on each tick. Deliberate:
      // we ONLY auto-refresh page 1 (the live view). A new close shifts every page, so
      // refreshing a deeper page would jump rows under a user browsing history — we'd
      // rather freeze that view than reflow it. The label can drift by one slot while
      // they linger off page 1; that resolves the moment they navigate or return to 1.
      const sig = closedSig(closed);
      if (sig !== lastClosedSigRef.current) {
        lastClosedSigRef.current = sig;
        if (historyPageRef.current === 1) void loadHistory(1);
      }
    });
    // subscribeToForex awaits the hub connection; if we unmount before it lands the
    // cleanup's unsubscribe is a no-op (we weren't subscribed yet) and the server would
    // keep pushing to a dead group. Undo the subscribe if it completes post-unmount.
    void signalRService.subscribeToForex(realtimeEnv).then(() => {
      if (!active) void signalRService.unsubscribeFromForex(realtimeEnv);
    });
    return () => {
      active = false;
      signalRService.removeForexListener(id, 'forexAccount');
      signalRService.removeForexListener(id, 'forexTrades');
      void signalRService.unsubscribeFromForex(realtimeEnv);
    };
  }, [health?.brokerReachable, realtimeEnv, loadHistory]);

  const onSyncStatement = useCallback(async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const r = await svc.syncStatement(3);
      setSyncMsg(`Synced last 3 months — ${r.inserted} new of ${r.parsed} found.`);
      // Refresh page 1: if already there, reload it; else navigating triggers the load.
      if (historyPageRef.current === 1) await loadHistory(1);
      else setHistoryPageNum(1);
      // Success is transient — don't pin it in the header. Replace any pending timer so a
      // rapid re-sync doesn't get its fresh message nuked early by an older one.
      if (syncMsgTimerRef.current) window.clearTimeout(syncMsgTimerRef.current);
      syncMsgTimerRef.current = window.setTimeout(() => setSyncMsg(null), 6000);
    } catch (e) {
      setSyncMsg(`Sync failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSyncing(false);
    }
  }, [svc, loadHistory]);

  // Invalidate the instrument cache when the FXCM account changes — the
  // tradable set and contract multipliers are account-specific, so if the
  // user switches credentials while the panel stays mounted the picker
  // and instrumentBySymbol map would otherwise stay pinned to the old
  // account. Clearing the one-shot guard makes the next loadAll refetch.
  // (First mount: accountId goes null→id, a harmless extra reset.)
  const accountId = health?.accountId ?? null;
  useEffect(() => {
    instrumentsFetched.current = false;
    setInstruments(FALLBACK_INSTRUMENTS);
  }, [accountId]);

  // Fast quote poll (4s) — only while the broker is reachable. The
  // /quotes endpoint is an in-memory offers snapshot, cheap to hit.
  const brokerReachable = health?.brokerReachable ?? false;
  useEffect(() => {
    if (!brokerReachable) {
      setQuotesBySymbol(new Map());
      return;
    }
    let cancelled = false;
    const pull = async () => {
      try {
        const qs = await svc.getQuotes();
        if (!cancelled) setQuotesBySymbol(new Map(qs.map((q) => [q.symbol, q])));
      } catch { /* transient — keep last quotes */ }
    };
    void pull();
    const id = setInterval(() => void pull(), 4_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [brokerReachable, svc]);

  // symbol → instrument, for per-instrument contract sizing.
  const instrumentBySymbol = React.useMemo(() => {
    const m = new Map<string, FxcmInstrument>();
    for (const i of instruments) m.set(i.symbol, i);
    return m;
  }, [instruments]);

  // Instruments grouped by asset class for the order-entry picker.
  // Only tradable instruments are offered — FXCM also returns view-only /
  // disabled offers, and selecting one would just fail at the broker
  // boundary. instrumentBySymbol above keeps the FULL set so the
  // positions/orders tables can still size a symbol that has since gone
  // view-only. Class display order: forex first, then alphabetical.
  const groupedInstruments = React.useMemo(() => {
    const byClass = new Map<string, FxcmInstrument[]>();
    for (const i of instruments) {
      if (!i.isTradingAllowed) continue;
      const arr = byClass.get(i.instrumentClass) ?? [];
      arr.push(i);
      byClass.set(i.instrumentClass, arr);
    }
    return [...byClass.entries()]
      .map(([cls, items]) => ({
        cls,
        label: instrumentClassLabel(cls),
        items: items.slice().sort((a, b) => a.displaySymbol.localeCompare(b.displaySymbol)),
      }))
      .sort((a, b) =>
        a.cls === 'forex' ? -1 : b.cls === 'forex' ? 1 : a.label.localeCompare(b.label));
  }, [instruments]);

  // Display label for a symbol — prefer the instrument's authoritative
  // displaySymbol (e.g. "SPX500", "XAU/USD") over fmtForexSymbol, which
  // mangles non-forex 6-char names ("SPX500" → "SPX/500").
  const displaySym = useCallback((symbol: string): string =>
    instrumentBySymbol.get(symbol)?.displaySymbol ?? fmtForexSymbol(symbol),
  [instrumentBySymbol]);

  // Contracts ↔ base-currency units, using the instrument's FXCM base unit
  // size (units = baseUnitSize × contracts). Returns NULL when the size
  // isn't known yet (instrument list not loaded / fetch failed) — callers
  // render "—" rather than a misleading 0; the backend is the authority on
  // the order amount regardless.
  const contractsForAmount = useCallback((symbol: string, amount: number): number | null => {
    const unit = instrumentBySymbol.get(symbol)?.baseUnitSize ?? 0;
    return unit > 0 ? amount / unit : null;
  }, [instrumentBySymbol]);
  const unitsForSymbol = useCallback((symbol: string, contracts: number): number | null => {
    const unit = instrumentBySymbol.get(symbol)?.baseUnitSize ?? 0;
    return unit > 0 ? Math.round(contracts * unit) : null;
  }, [instrumentBySymbol]);

  const onToggleEnabled = useCallback(async () => {
    if (!settings) return;
    try {
      const next = !settings.tradingEnabled;
      const updated = await svc.setEnabled(next);
      setSettings(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [settings, svc]);

  // Submit an already-validated order to the broker. Demo calls this
  // directly from onPlaceOrder; live routes through the confirm dialog.
  const submitOrder = useCallback(async (p: PendingSubmit) => {
    if (submitGuardRef.current) return;   // re-entrancy guard — blocks double-click
    submitGuardRef.current = true;
    setOrderResult(null);
    setOrderSubmitting(true);
    try {
      const o = p.order;
      const r = p.kind === 'market'
        ? await svc.placeMarketOrder(p.order)
        : await svc.placeEntryOrder(p.order);
      const prot = [
        o.stopPrice != null ? `SL ${o.stopPrice}` : null,
        o.limitPrice != null ? `TP ${o.limitPrice}` : null,
      ].filter(Boolean).join(', ');
      const kindLabel = p.kind === 'market'
        ? 'market'
        : `${p.order.orderType} entry @ ${p.order.entryPrice}`;
      // SL/TP are echoed as "requested" — the protective orders are
      // submitted on the same valuemap but we don't read the trade row
      // back here to confirm FXCM actually attached them. Verify on the
      // positions table / FXCM until that read-back lands.
      setOrderResult(
        `Order ${r.orderId} submitted: ${o.side} ${o.contracts} ${displaySym(o.symbol)} (${kindLabel})`
        + (prot ? ` · ${prot} requested` : ''));
      // Clear the rate fields so a stale entry/SL/TP/band can't silently
      // carry over to the next order — likely wrong-side after a flip.
      setOrderStop('');
      setOrderLimit('');
      setOrderEntryPrice('');
      setOrderRangePips('');
      setOrderTrailPips('');
      void loadAll();
    } catch (e) {
      setOrderResult(`Failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      submitGuardRef.current = false;
      setOrderSubmitting(false);
    }
  }, [svc, displaySym, loadAll]);

  const onPlaceOrder = useCallback(() => {
    setOrderResult(null);
    // Strict numeric parse: Number() (unlike parseFloat) rejects partial
    // input — "1abc", "1,0950" → NaN — so a malformed entry can never be
    // silently truncated into a wrong (and risk-bearing) value.
    // Contracts are whole numbers — FXCM trades in discrete contracts
    // (1 forex contract = 1,000 units / one micro lot), so a fractional
    // value would be rejected by the broker as an invalid size.
    const contracts = strictPositiveNumber(orderContracts);
    if (contracts == null || !Number.isInteger(contracts)) {
      setOrderResult('Contracts must be a whole number ≥ 1 — FXCM trades discrete contracts');
      return;
    }
    // SL/TP optional; a non-empty field must parse to a positive rate.
    let stopPrice: number | undefined;
    let limitPrice: number | undefined;
    if (orderStop.trim() !== '') {
      stopPrice = strictPositiveNumber(orderStop) ?? undefined;
      if (stopPrice == null) { setOrderResult('Stop-loss must be a positive rate, or left blank'); return; }
    }
    if (orderLimit.trim() !== '') {
      limitPrice = strictPositiveNumber(orderLimit) ?? undefined;
      if (limitPrice == null) { setOrderResult('Take-profit must be a positive rate, or left blank'); return; }
    }
    // Trailing stop — optional, only meaningful with a stop-loss attached.
    let trailStepPips: number | undefined;
    if (orderTrailPips.trim() !== '') {
      const t = strictPositiveNumber(orderTrailPips);
      if (t == null) { setOrderResult('Trailing step must be a positive number of pips, or left blank'); return; }
      if (stopPrice == null) { setOrderResult('Set a stop-loss for the trailing step to follow'); return; }
      trailStepPips = Math.round(t);
    }

    const inst = instrumentBySymbol.get(orderSymbol);
    const pointSize = inst?.pointSize ?? (orderSymbol.toUpperCase().includes('JPY') ? 0.01 : 0.0001);
    const isEntry = orderType === 'limit' || orderType === 'stop' || orderType === 'range';
    const isRanged = orderType === 'marketRange' || orderType === 'range';

    // Price band for ranged orders. Market Range centres on the live mid;
    // Range Entry centres on the entry price.
    let rateMin: number | undefined;
    let rateMax: number | undefined;
    if (isRanged) {
      const pips = strictPositiveNumber(orderRangePips);
      if (pips == null) { setOrderResult('Range must be a positive number of pips'); return; }
      let ref: number | null;
      if (orderType === 'marketRange') {
        const q = quotesBySymbol.get(orderSymbol);
        ref = q ? (q.bid + q.ask) / 2 : null;
        if (ref == null) { setOrderResult('No live quote yet for this instrument — wait for the quote strip'); return; }
      } else {
        ref = strictPositiveNumber(orderEntryPrice);
      }
      const band = ref != null ? computeBand(ref, pips, pointSize) : null;
      if (band == null) { setOrderResult('Could not compute the price band — check the range and entry price'); return; }
      rateMin = band.min;
      rateMax = band.max;
    }

    // Time-in-force. GTD also needs a future expiry.
    const tif = orderTif.trim().toUpperCase();
    let expireAt: string | undefined;
    if (tif === 'GTD') {
      if (orderExpireAt.trim() === '') { setOrderResult('Pick an expiry date/time for a GTD order'); return; }
      const d = new Date(orderExpireAt);
      if (Number.isNaN(d.getTime()) || d.getTime() <= Date.now()) {
        setOrderResult('GTD expiry must be a valid future date/time');
        return;
      }
      expireAt = d.toISOString();
    }

    let pending: PendingSubmit;
    if (!isEntry) {
      pending = {
        kind: 'market',
        order: {
          symbol: orderSymbol, side: orderSide, contracts, stopPrice, limitPrice,
          rateMin, rateMax, trailStepPips,
          timeInForce: tif === 'IOC' || tif === 'FOK' ? tif : undefined,
        },
      };
    } else {
      const entryPrice = strictPositiveNumber(orderEntryPrice);
      if (entryPrice == null) { setOrderResult('Entry price must be a positive rate'); return; }
      pending = {
        kind: 'entry',
        order: {
          symbol: orderSymbol, side: orderSide, contracts,
          orderType: orderType as 'limit' | 'stop' | 'range',
          entryPrice, stopPrice, limitPrice, rateMin, rateMax, trailStepPips,
          timeInForce: tif === 'GTC' || tif === 'DAY' || tif === 'GTD' ? tif : undefined,
          expireAt,
        },
      };
    }

    // Live trading is real money — never submit without an explicit
    // second click on the confirmation dialog. Demo submits straight away.
    if (live) {
      setConfirmOrder(pending);
      return;
    }
    void submitOrder(pending);
  }, [orderSymbol, orderSide, orderType, orderEntryPrice, orderRangePips, orderContracts,
      orderStop, orderTrailPips, orderLimit, orderTif, orderExpireAt,
      instrumentBySymbol, quotesBySymbol, live, submitOrder]);

  // Cancel a pending order. orderId is the FXCM-native string id from
  // FxcmOrderDto.orderId (the cancel endpoint expects that). Live mode confirms
  // first (real-money state change).
  const onCancelOrder = useCallback((orderId: string, label: string) => {
    const doCancel = async () => {
      try {
        await svc.cancelOrder(orderId);
        void loadAll();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    };
    if (live) {
      actionGuardRef.current = false;   // arm a fresh confirmation
      setConfirmAction({ message: `Cancel pending order ${label} on the live account.`, run: () => void doCancel() });
      return;
    }
    void doCancel();
  }, [svc, loadAll, live]);

  // Close all open positions on a pair. Live mode confirms first — a close
  // is a real-money order, same as opening one.
  const onClosePosition = useCallback((symbol: string) => {
    const doClose = async () => {
      try {
        await svc.closePosition(symbol);
        void loadAll();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    };
    if (live) {
      actionGuardRef.current = false;   // arm a fresh confirmation
      setConfirmAction({
        message: `Close ALL ${displaySym(symbol)} positions on the live account — this is a real-money order.`,
        run: () => void doClose(),
      });
      return;
    }
    void doClose();
  }, [svc, loadAll, live, displaySym]);

  // Toggle the inline SL/TP editor for a position; reset its fields on open.
  const onEditProtective = useCallback((p: FxcmPosition) => {
    setEditingTradeId(prev => (prev === p.tradeId ? null : p.tradeId));
    setEditStop(''); setEditLimit('');
    setEditClearStop(false); setEditClearLimit(false);
    setEditMsg(null);
  }, []);

  // Save the SL/TP change for a position. Blank field = leave that side; the
  // clear checkbox removes it. Live mode confirms first (real-money change).
  const onSaveProtective = useCallback((p: FxcmPosition) => {
    setEditOk(false);   // validation/reject messages render as errors by default
    const req: ModifyProtectiveOrders = {};
    // strictPositiveNumber rejects partial/garbage input (NaN) rather than
    // truncating it — same guard the order form uses on these risk-bearing rates.
    if (editClearStop) req.clearStop = true;
    else if (editStop.trim() !== '') {
      const n = strictPositiveNumber(editStop);
      if (n == null) { setEditMsg('Stop must be a number greater than 0.'); return; }
      req.rateStop = n;
    }
    if (editClearLimit) req.clearLimit = true;
    else if (editLimit.trim() !== '') {
      const n = strictPositiveNumber(editLimit);
      if (n == null) { setEditMsg('Limit must be a number greater than 0.'); return; }
      req.rateLimit = n;
    }

    const touchesStop = req.clearStop || req.rateStop != null;
    const touchesLimit = req.clearLimit || req.rateLimit != null;
    if (!touchesStop && !touchesLimit) {
      setEditMsg('Enter a stop/limit rate or check “clear”.'); return;
    }

    const doSave = async () => {
      setEditSaving(true); setEditMsg(null);
      try {
        const res = await svc.setProtectiveOrders(p.tradeId, req);
        // Keep the editor open so the confirmation is visible; reset the inputs
        // and refresh positions. The user dismisses with Cancel.
        setEditStop(''); setEditLimit('');
        setEditClearStop(false); setEditClearLimit(false);
        void loadAll();
        setEditOk(true);
        setEditMsg(res.message ?? 'Protective orders updated');
      } catch (e) {
        // Surfaces FXCM rejects (e.g. "stop on the wrong side of the market").
        setEditMsg(e instanceof Error ? e.message : String(e));
      } finally {
        setEditSaving(false);
      }
    };
    if (live) {
      // Spell out the exact change in the confirm dialog — on a real-money
      // action the rates ARE the change, and the inputs can be edited between
      // Save and Confirm, so the captured req must be what's shown.
      const parts = [
        req.clearStop ? 'clear stop' : req.rateStop != null ? `set stop=${req.rateStop}` : null,
        req.clearLimit ? 'clear limit' : req.rateLimit != null ? `set limit=${req.rateLimit}` : null,
      ].filter(Boolean).join(', ');
      actionGuardRef.current = false;
      setConfirmAction({
        message: `Modify SL/TP on ${displaySym(p.symbol)} ${p.side} — ${parts}. Real-money protective-order change on the live account.`,
        run: () => void doSave(),
      });
      return;
    }
    void doSave();
  }, [svc, loadAll, live, displaySym, editStop, editLimit, editClearStop, editClearLimit]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading FXCM forex panel…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Live real-money banner ───────────────────────────────── */}
      {live && (
        <Card className="border-2 border-red-500 bg-red-500/10">
          <CardContent className="p-3 flex items-center gap-2 text-sm font-semibold text-red-500">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            LIVE TRADING — orders here place real trades with real money on your funded FXCM account.
          </CardContent>
        </Card>
      )}

      {/* ── Live action confirmation (close / cancel) ────────────── */}
      {confirmAction && (
        <Card className="border-2 border-red-500 bg-red-500/10">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-500">
              <ShieldAlert className="h-4 w-4" /> Confirm live action — real money
            </div>
            <div className="text-sm">{confirmAction.message}</div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => {
                  if (actionGuardRef.current) return;   // re-entrancy guard — blocks double-click
                  actionGuardRef.current = true;
                  const a = confirmAction;
                  setConfirmAction(null);
                  a.run();
                }}
              >
                Confirm
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmAction(null)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Status banner ────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span
              className={`inline-block h-2 w-2 rounded-full ${health?.brokerReachable ? 'bg-green-500' : 'bg-red-500'}`}
              aria-hidden
            />
            <span className="text-sm font-medium">
              {health?.brokerReachable ? 'FXCM connected' : 'FXCM offline'}
            </span>
            {health?.accountId && (
              <span className="text-xs text-muted-foreground">· acct {health.accountId}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground flex-1 truncate">{health?.message}</div>
          <Button
            variant={settings?.tradingEnabled ? 'default' : 'outline'}
            size="sm"
            onClick={onToggleEnabled}
            disabled={!settings}
          >
            <Power className="h-3 w-3 mr-1" />
            {settings?.tradingEnabled ? 'Trading ON' : 'Trading OFF'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void loadAll()}>
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-500/50">
          <CardContent className="p-3 flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" /> {error}
          </CardContent>
        </Card>
      )}

      {/* ── Account snapshot ──────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Field label="Account" value={account?.accountId ?? '-'} />
          <Field label="Status" value={account?.status ?? '-'} />
          <Field label="Balance" value={fmtMoney(account?.balance)} />
          <Field label="Equity" value={fmtMoney(account?.equity)} />
          <Field label="Used margin" value={fmtMoney(account?.usedMargin)} />
          <Field label="Usable margin" value={fmtMoney(account?.usableMargin)} />
          <Field
            label="Margin level"
            value={account?.marginLevelPercent != null ? `${account.marginLevelPercent.toFixed(0)}%` : '—'}
          />
          <Field
            label="Maint. margin"
            value={account?.maintMarginPercent != null ? `${account.maintMarginPercent.toFixed(0)}%` : '—'}
          />
          <Field label="Day P&L" value={fmtMoney(account?.dayPL)} className={pnlClass(account?.dayPL)} />
          <Field label="Open P&L" value={fmtMoney(account?.grossPL)} className={pnlClass(account?.grossPL)} />
          <Field label="Locked" value={account?.isLocked ? 'YES' : 'no'} />
        </CardContent>
      </Card>

      {/* ── Quick order form ──────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open trade (market)</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          {/* Live bid/ask strip for the selected instrument. */}
          <QuoteStrip
            quote={quotesBySymbol.get(orderSymbol) ?? null}
            instrument={instrumentBySymbol.get(orderSymbol) ?? null}
            displaySymbol={instrumentBySymbol.get(orderSymbol)?.displaySymbol
              ?? fmtForexSymbol(orderSymbol)}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Instrument</label>
            <Select value={orderSymbol} onValueChange={setOrderSymbol}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-80">
                {groupedInstruments.map(g => (
                  <SelectGroup key={g.cls}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.items.map(i => (
                      <SelectItem key={i.symbol} value={i.symbol}>{i.displaySymbol}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Side</label>
            <Select value={orderSide} onValueChange={(v) => setOrderSide(v as 'buy' | 'sell')}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="buy">Buy</SelectItem>
                <SelectItem value="sell">Sell</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Order type</label>
            <Select
              value={orderType}
              onValueChange={(v) => {
                setOrderType(v as OrderType);
                // TIF options differ per family — reset so a stale IOC
                // can't ride along onto an entry order (or vice-versa).
                setOrderTif('');
                setOrderExpireAt('');
              }}
            >
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="market">Market</SelectItem>
                <SelectItem value="marketRange">Market Range</SelectItem>
                <SelectItem value="limit">Limit</SelectItem>
                <SelectItem value="stop">Stop</SelectItem>
                <SelectItem value="range">Range Entry</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(orderType === 'limit' || orderType === 'stop' || orderType === 'range') && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Entry price</label>
              <input
                type="text"
                inputMode="decimal"
                value={orderEntryPrice}
                onChange={(e) => setOrderEntryPrice(e.target.value)}
                className="w-28 px-2 py-1 text-sm rounded border bg-background"
                placeholder="trigger rate"
              />
              <span className="text-[10px] text-muted-foreground">
                {orderSide === 'buy' ? 'Buy' : 'Sell'} {ORDER_TYPE_LABEL[orderType]}
              </span>
            </div>
          )}
          {(orderType === 'marketRange' || orderType === 'range') && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Range (± pips)</label>
              <input
                type="text"
                inputMode="decimal"
                value={orderRangePips}
                onChange={(e) => setOrderRangePips(e.target.value)}
                className="w-24 px-2 py-1 text-sm rounded border bg-background"
                placeholder="pips"
              />
              <span className="text-[10px] text-muted-foreground">
                {orderType === 'marketRange' ? 'slippage band' : 'band around entry'}
              </span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Contracts</label>
            <input
              type="text"
              inputMode="numeric"
              value={orderContracts}
              onChange={(e) => setOrderContracts(e.target.value)}
              className="w-24 px-2 py-1 text-sm rounded border bg-background"
              placeholder="1"
            />
            <span className="text-[10px] text-muted-foreground">
              ≈ {unitsForSymbol(orderSymbol, parseFloat(orderContracts) || 0)?.toLocaleString() ?? '—'} units
              {instrumentBySymbol.get(orderSymbol)?.instrumentClass === 'forex' && ' · 1 contract = 1 micro lot'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Stop-loss</label>
            <input
              type="text"
              inputMode="decimal"
              value={orderStop}
              onChange={(e) => setOrderStop(e.target.value)}
              className="w-28 px-2 py-1 text-sm rounded border bg-background"
              placeholder="optional"
            />
            <span className="text-[10px] text-muted-foreground">attached stop rate</span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Trail (pips)</label>
            <input
              type="text"
              inputMode="decimal"
              value={orderTrailPips}
              onChange={(e) => setOrderTrailPips(e.target.value)}
              className="w-24 px-2 py-1 text-sm rounded border bg-background"
              placeholder="optional"
            />
            <span className="text-[10px] text-muted-foreground">trailing-stop step</span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Take-profit</label>
            <input
              type="text"
              inputMode="decimal"
              value={orderLimit}
              onChange={(e) => setOrderLimit(e.target.value)}
              className="w-28 px-2 py-1 text-sm rounded border bg-background"
              placeholder="optional"
            />
            <span className="text-[10px] text-muted-foreground">attached limit rate</span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Time in force</label>
            <Select value={orderTif} onValueChange={setOrderTif}>
              <SelectTrigger className="w-28"><SelectValue placeholder="Default" /></SelectTrigger>
              <SelectContent>
                {orderType === 'limit' || orderType === 'stop' || orderType === 'range' ? (
                  <>
                    <SelectItem value="GTC">GTC</SelectItem>
                    <SelectItem value="DAY">Day</SelectItem>
                    <SelectItem value="GTD">GTD</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="default">Default</SelectItem>
                    <SelectItem value="IOC">IOC</SelectItem>
                    <SelectItem value="FOK">FOK</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          {orderTif.toUpperCase() === 'GTD' && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">GTD expiry</label>
              <input
                type="datetime-local"
                value={orderExpireAt}
                onChange={(e) => setOrderExpireAt(e.target.value)}
                className="w-44 px-2 py-1 text-sm rounded border bg-background"
              />
            </div>
          )}
          <Button
            onClick={onPlaceOrder}
            disabled={orderSubmitting || !settings?.tradingEnabled || confirmOrder != null}
            title={!settings?.tradingEnabled ? 'Enable trading first' : ''}
            className={live ? 'bg-red-600 hover:bg-red-700 text-white' : undefined}
          >
            {orderSubmitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            {orderSide === 'buy' ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
            {live ? 'Review order' : 'Submit'}
          </Button>
          {orderResult && (
            <div className="basis-full text-xs text-muted-foreground">{orderResult}</div>
          )}

          {/* Live-trading confirmation — a mandatory second click before
              any real-money order leaves the browser. */}
          {confirmOrder && (
            <div className="basis-full rounded-md border-2 border-red-500 bg-red-500/10 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-red-500">
                <ShieldAlert className="h-4 w-4" />
                Confirm LIVE order — this places a REAL order with REAL money
              </div>
              <div className="text-sm font-mono">
                {confirmOrder.order.side.toUpperCase()}{' '}
                {confirmOrder.kind === 'entry' ? confirmOrder.order.orderType.toUpperCase() + ' ' : ''}
                {confirmOrder.order.contracts} {displaySym(confirmOrder.order.symbol)}
                {confirmOrder.kind === 'entry' && <> @ {confirmOrder.order.entryPrice}</>}
                {confirmOrder.order.rateMin != null && confirmOrder.order.rateMax != null && (
                  <> · band [{confirmOrder.order.rateMin.toFixed(5)}–{confirmOrder.order.rateMax.toFixed(5)}]</>
                )}
                {confirmOrder.order.stopPrice != null && <> · SL {confirmOrder.order.stopPrice}</>}
                {confirmOrder.order.trailStepPips != null && <> (trail {confirmOrder.order.trailStepPips}p)</>}
                {confirmOrder.order.limitPrice != null && <> · TP {confirmOrder.order.limitPrice}</>}
                {confirmOrder.order.timeInForce != null && <> · {confirmOrder.order.timeInForce}</>}
                {' '}≈ {unitsForSymbol(confirmOrder.order.symbol, confirmOrder.order.contracts)?.toLocaleString() ?? '—'} units
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={orderSubmitting}
                  onClick={() => {
                    const req = confirmOrder;
                    setConfirmOrder(null);
                    void submitOrder(req);
                  }}
                >
                  {orderSubmitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Confirm &amp; place real order
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={orderSubmitting}
                  onClick={() => setConfirmOrder(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Price chart (selected instrument) ────────────────────── */}
      {brokerReachable && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Chart</CardTitle>
          </CardHeader>
          <CardContent>
            <ForexChart
              svc={svc}
              symbol={orderSymbol}
              quote={quotesBySymbol.get(orderSymbol) ?? null}
              positions={positions.filter(p => p.symbol === orderSymbol)}
            />
          </CardContent>
        </Card>
      )}

      {/* ── Open positions ───────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open positions</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-2">Pair</th>
                <th className="p-2">Side</th>
                <th className="p-2">Opened</th>
                <th className="p-2 text-right">Contracts</th>
                <th className="p-2 text-right">Open</th>
                <th className="p-2 text-right">Current</th>
                <th className="p-2 text-right">SL</th>
                <th className="p-2 text-right">TP</th>
                <th className="p-2 text-right">Pips</th>
                <th className="p-2 text-right">P&amp;L</th>
                <th className="p-2 text-right">P&amp;L %</th>
                <th className="p-2 text-right">Swap</th>
                <th className="p-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 && (
                <tr><td colSpan={13} className="p-4 text-center text-muted-foreground text-xs">No open positions</td></tr>
              )}
              {positions.map(p => (
                <React.Fragment key={p.tradeId}>
                <tr className="border-t">
                  <td className="p-2">{displaySym(p.symbol)}</td>
                  <td className="p-2">{p.side}</td>
                  <td className="p-2 text-xs whitespace-nowrap">{p.openedAt ? new Date(p.openedAt).toLocaleString() : '—'}</td>
                  <td className="p-2 text-right">{contractsForAmount(p.symbol, p.amount)?.toFixed(0) ?? '—'}</td>
                  <td className="p-2 text-right">{fmtRate(p.openRate, p.symbol)}</td>
                  <td className="p-2 text-right">{fmtRate(p.currentRate, p.symbol)}</td>
                  <td className="p-2 text-right">{p.stop != null ? fmtRate(p.stop, p.symbol) : '—'}</td>
                  <td className="p-2 text-right">{p.limit != null ? fmtRate(p.limit, p.symbol) : '—'}</td>
                  <td className={`p-2 text-right ${pnlClass(p.pips)}`}>{p.pips != null ? p.pips.toFixed(1) : '—'}</td>
                  <td className={`p-2 text-right ${pnlClass(p.unrealizedPnL)}`}>{fmtMoney(p.unrealizedPnL)}</td>
                  <td className={`p-2 text-right ${pnlClass(p.unrealizedPnLPercent)}`}>
                    {p.unrealizedPnLPercent != null ? `${p.unrealizedPnLPercent.toFixed(2)}%` : '-'}
                  </td>
                  <td className={`p-2 text-right ${pnlClass(p.rollover)}`}>{p.rollover != null ? fmtMoney(p.rollover) : '—'}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-1" onClick={() => onEditProtective(p)}>SL/TP</Button>
                    <Button size="sm" variant="outline" onClick={() => onClosePosition(p.symbol)}>Close</Button>
                  </td>
                </tr>
                {editingTradeId === p.tradeId && (
                  <tr className="border-t bg-muted/30">
                    <td colSpan={13} className="p-2">
                      <div className="flex items-center gap-3 flex-wrap text-xs">
                        <span className="font-medium">Modify SL/TP — {displaySym(p.symbol)} {p.side}</span>
                        <label className="flex items-center gap-1">
                          Stop
                          <input
                            type="number" step="any" min={0} className="input-field w-28"
                            value={editStop} disabled={editClearStop} placeholder="leave unchanged"
                            onChange={e => setEditStop(e.target.value)} />
                        </label>
                        <label className="flex items-center gap-1">
                          <input type="checkbox" checked={editClearStop}
                            onChange={e => setEditClearStop(e.target.checked)} /> clear stop
                        </label>
                        <label className="flex items-center gap-1">
                          Limit
                          <input
                            type="number" step="any" min={0} className="input-field w-28"
                            value={editLimit} disabled={editClearLimit} placeholder="leave unchanged"
                            onChange={e => setEditLimit(e.target.value)} />
                        </label>
                        <label className="flex items-center gap-1">
                          <input type="checkbox" checked={editClearLimit}
                            onChange={e => setEditClearLimit(e.target.checked)} /> clear limit
                        </label>
                        <Button size="sm" disabled={editSaving} onClick={() => onSaveProtective(p)}>
                          {editSaving ? 'Saving…' : 'Save'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditingTradeId(null); setConfirmAction(null); }}>Cancel</Button>
                        {editMsg && <span className={editOk ? 'text-green-600' : 'text-destructive'}>{editMsg}</span>}
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Blank = leave unchanged. A stop must sit on the losing side, a limit on the winning side — FXCM rejects otherwise.
                      </p>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ── Recent orders ────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent orders</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-2">Time</th>
                <th className="p-2">Pair</th>
                <th className="p-2">Type</th>
                <th className="p-2">Side</th>
                <th className="p-2 text-right">Contracts</th>
                <th className="p-2 text-right">Rate</th>
                <th className="p-2">TIF</th>
                <th className="p-2">Status</th>
                <th className="p-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={9} className="p-4 text-center text-muted-foreground text-xs">No recent orders</td></tr>
              )}
              {orders.map(o => (
                <tr key={o.orderId} className="border-t">
                  <td className="p-2 text-xs">{o.submittedAt ? new Date(o.submittedAt).toLocaleString() : '-'}</td>
                  <td className="p-2">{displaySym(o.symbol)}</td>
                  <td className="p-2 text-xs">{orderTypeLabel(o.type)}</td>
                  <td className="p-2">{o.side}</td>
                  <td className="p-2 text-right">{contractsForAmount(o.symbol, o.amount)?.toFixed(0) ?? '—'}</td>
                  <td className="p-2 text-right">{fmtRate(o.rate, o.symbol)}</td>
                  <td className="p-2 text-xs" title={o.expireAt ? `expires ${new Date(o.expireAt).toLocaleString()}` : undefined}>
                    {o.timeInForce || '—'}{o.expireAt ? '*' : ''}
                  </td>
                  <td className="p-2"><span className="text-xs">{o.status}</span></td>
                  <td className="p-2 text-right">
                    {isCancellable(o.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onCancelOrder(o.orderId, displaySym(o.symbol))}
                      >
                        Cancel
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ── Trade history (paginated, store-backed) ──────────────── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">Trade history</CardTitle>
          <div className="flex items-center gap-2">
            {syncMsg && <span className="text-[11px] text-muted-foreground">{syncMsg}</span>}
            <Button size="sm" variant="outline" onClick={onSyncStatement} disabled={syncing || !health?.brokerReachable}>
              {syncing && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Sync from FXCM
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-2">Pair</th>
                <th className="p-2">Side</th>
                <th className="p-2 text-right">Contracts</th>
                <th className="p-2 text-right">Open</th>
                <th className="p-2 text-right">Close</th>
                <th className="p-2 text-right">Pips</th>
                <th className="p-2 text-right">Gross</th>
                <th className="p-2 text-right">Net</th>
                <th className="p-2">Reason</th>
                <th className="p-2">Closed</th>
              </tr>
            </thead>
            <tbody>
              {(historyPage?.items.length ?? 0) === 0 && (
                <tr><td colSpan={10} className="p-4 text-center text-muted-foreground text-xs">
                  {historyLoading ? 'Loading…'
                    : historyPage === null && !health?.brokerReachable ? 'Reconnecting to FXCM…'
                    : 'No trade history yet — use “Sync from FXCM” to pull past trades.'}
                </td></tr>
              )}
              {(historyPage?.items ?? []).map(t => {
                const reason = closeReasonLabel(t.closeReason, t.closeReasonIsGuess);
                return (
                <tr key={t.tradeId} className="border-t">
                  <td className="p-2">{displaySym(t.symbol)}</td>
                  <td className="p-2">{t.side}</td>
                  <td className="p-2 text-right">{contractsForAmount(t.symbol, t.amount)?.toFixed(0) ?? '—'}</td>
                  <td className="p-2 text-right">{fmtRate(t.openRate, t.symbol)}</td>
                  <td className="p-2 text-right">{fmtRate(t.closeRate, t.symbol)}</td>
                  <td className={`p-2 text-right ${pnlClass(t.pipPL)}`}>{t.pipPL !== 0 ? t.pipPL.toFixed(1) : '—'}</td>
                  <td className={`p-2 text-right ${pnlClass(t.grossPL)}`}>{fmtMoney(t.grossPL)}</td>
                  <td className={`p-2 text-right ${pnlClass(t.netPL)}`}>{fmtMoney(t.netPL)}</td>
                  <td className="p-2 text-xs">
                    <span
                      className={reason.guess ? 'text-muted-foreground italic' : ''}
                      title={reason.guess ? 'Inferred from the close rate vs the saved stop/limit — not an FXCM field' : undefined}
                    >
                      {reason.text}
                    </span>
                  </td>
                  <td className="p-2 text-xs">{new Date(t.closeTime).toLocaleString()}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {historyError && (
            <p className="px-2 pt-2 text-[11px] text-red-500">Couldn’t load history: {historyError}</p>
          )}
          {/* Pagination */}
          {historyPage && historyPage.total > 0 && (
            <div className="flex items-center justify-between p-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                {historyLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                {((historyPageNum - 1) * HISTORY_PAGE_SIZE) + 1}–
                {Math.min(historyPageNum * HISTORY_PAGE_SIZE, historyPage.total)} of {historyPage.total}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  size="sm" variant="outline" className="h-6 px-2"
                  disabled={historyPageNum <= 1 || historyLoading}
                  onClick={() => setHistoryPageNum(n => Math.max(1, n - 1))}
                >Prev</Button>
                <span>Page {historyPageNum} of {Math.max(1, Math.ceil(historyPage.total / HISTORY_PAGE_SIZE))}</span>
                <Button
                  size="sm" variant="outline" className="h-6 px-2"
                  disabled={historyPageNum >= Math.ceil(historyPage.total / HISTORY_PAGE_SIZE) || historyLoading}
                  onClick={() => setHistoryPageNum(n => n + 1)}
                >Next</Button>
              </div>
            </div>
          )}
          <p className="p-2 text-[11px] text-muted-foreground">
            Kept across days (FXCM itself only retains the current trading day). “Sync from FXCM” pulls the
            last 3 months from your account statement. Net = gross + commission + rollover + dividends, in your
            account currency. Reasons marked “?” are inferred from the close rate vs the trade's saved stop/limit;
            statement-imported rows have no reason.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`font-mono ${className ?? ''}`}>{value}</span>
    </div>
  );
}

/** Live bid/ask/spread strip for the currently-selected instrument.
 *  Spread is shown in points (raw spread / instrument point size) — the
 *  conventional forex "pips" figure — falling back to the raw rate diff
 *  when point size is unknown. Renders placeholders until the first
 *  quote for the instrument arrives. */
function QuoteStrip({
  quote, instrument, displaySymbol,
}: {
  quote: FxcmQuote | null;
  instrument: FxcmInstrument | null;
  displaySymbol: string;
}) {
  // Decimal places from the instrument's point size + 1 fractional digit
  // (the conventional "fractional pip" / sub-point precision): EUR/USD
  // pointSize 0.0001 → 5dp, USD/JPY 0.01 → 3dp, gold/indices scale too.
  // Falls back to the JPY-vs-other heuristic when point size is unknown
  // (instrument not yet loaded).
  const isJpy = displaySymbol.toUpperCase().includes('JPY');
  const dp = instrument && instrument.pointSize > 0
    ? Math.max(0, Math.round(-Math.log10(instrument.pointSize))) + 1
    : (isJpy ? 3 : 5);
  const spreadPts = quote && instrument && instrument.pointSize > 0
    ? quote.spread / instrument.pointSize
    : null;
  return (
    <div className="basis-full flex items-center gap-6 rounded border bg-muted/30 px-3 py-2 text-sm">
      <span className="font-medium">{displaySymbol}</span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] text-muted-foreground">Bid</span>
        <span className="font-mono text-red-500">
          {quote ? quote.bid.toFixed(dp) : '—'}
        </span>
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] text-muted-foreground">Ask</span>
        <span className="font-mono text-green-500">
          {quote ? quote.ask.toFixed(dp) : '—'}
        </span>
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] text-muted-foreground">Spread</span>
        <span className="font-mono">
          {spreadPts != null ? `${spreadPts.toFixed(1)} pts` : '—'}
        </span>
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] text-muted-foreground">Day range</span>
        <span className="font-mono">
          {quote && quote.low > 0 && quote.high > 0
            ? `${quote.low.toFixed(dp)} – ${quote.high.toFixed(dp)}`
            : '—'}
        </span>
      </span>
      {!quote && (
        <span className="text-[10px] text-muted-foreground">waiting for first tick…</span>
      )}
    </div>
  );
}
